import React, { useCallback } from 'react';
import * as Clipboard from 'expo-clipboard';
import { NotificationFeedbackType } from 'expo-haptics';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { Linking, ScrollView, StyleSheet } from 'react-native';

import {
  Container,
  IconLink,
  theme,
  triggerHapticNotificationFeedback,
  useNoticeBannersStackContext,
  useResponsive,
  useShareTextDialog,
} from '@sg/garnish';

import { GroupOrderModal, GroupOrderShare } from '@order/features/group-ordering';
import { useTelemetry } from '@order/Telemetry';
import { getEnvVars } from '@order/utils';

import { NotFoundView } from '../../components/NotFoundView';

// ─────────────────────────────────────────────────────────────────────────────

export const GroupOrderShareScreen = () => {
  const router = useRouter();
  const { joinCode } = useLocalSearchParams<GroupOrderShareScreenParams>();
  const { push: addBanner } = useNoticeBannersStackContext();
  const { share } = useShareTextDialog();
  const { formatMessage } = useIntl();
  const { track } = useTelemetry();
  const { match } = useResponsive();

  // ─── Derived ──────────────────────────────────────────────────────────

  const shareUrl = joinCode ? buildGroupOrderShareUrl(joinCode) : '';
  const displayUrl = shareUrl ? stripShareUrlProtocol(shareUrl) : '';

  const shareMessage = formatMessage(messages.shareMessage, { share_url: shareUrl });
  const shareSubject = formatMessage(messages.shareSubject);
  const copyLinkSuccessText = formatMessage(messages.copyLinkSuccess);
  const contentContainerStyles = [styles.contentContainer, match([undefined, styles.contentContainerSM])]; // prettier-ignore

  // ─── Handlers ─────────────────────────────────────────────────────────

  const navigateToBag = useCallback(() => {
    router.replace('/bag');
  }, [router]);

  const closeScreen = useCallback(() => {
    router.back();
  }, [router]);

  const handleCopyLink = useCallback(async (): Promise<boolean> => {
    const hasCopied = await Clipboard.setStringAsync(shareUrl);

    if (!hasCopied) return false;

    track('group_ordering.share.copy_link_tapped');

    await triggerHapticNotificationFeedback(NotificationFeedbackType.Success);

    addBanner(
      { id: 'go-share-success-banner', palette: 'success', text: copyLinkSuccessText, autoHideTimeout: 1800 }, // prettier-ignore
      true,
    );

    return true;
  }, [addBanner, copyLinkSuccessText, shareUrl, track]);

  const handleShareEmail = useCallback(async () => {
    track('group_ordering.share.email_tapped');

    await Linking.openURL(
      `mailto:?subject=${encodeURIComponent(shareSubject)}&body=${encodeURIComponent(shareMessage)}`,
    );
  }, [shareMessage, shareSubject, track]);

  const handleShareMessage = useCallback(async () => {
    track('group_ordering.share.message_tapped');

    await Linking.openURL(`sms:?body=${encodeURIComponent(shareMessage)}`);
  }, [shareMessage, track]);

  const handleShareMore = useCallback(async () => {
    track('group_ordering.share.more_tapped');

    try {
      await share({ url: shareUrl, message: shareMessage });
    } catch {}
  }, [share, shareMessage, shareUrl, track]);

  // ─────────────────────────────────────────────────────────────────────

  if (!joinCode) return <NotFoundView />;

  return (
    <GroupOrderModal onClose={closeScreen}>
      <ScrollView showsVerticalScrollIndicator={false} bounces={false}>
        <Container style={contentContainerStyles}>
          <IconLink
            name="IconClose"
            accessibilityLabel={formatMessage(messages.closeA11yLabel)}
            onPress={closeScreen}
            width={40}
            height={40}
            palette="dark-kale"
            style={styles.closeButton}
          />

          <GroupOrderShare.Header />
          <GroupOrderShare.LinkRow joinLink={displayUrl} onCopyLink={handleCopyLink} />

          <GroupOrderShare.ShareActions
            onShareEmail={handleShareEmail}
            onShareMessage={handleShareMessage}
            onShareMore={handleShareMore}
          />

          <GroupOrderShare.DoneButton onPress={navigateToBag} />
        </Container>
      </ScrollView>
    </GroupOrderModal>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildGroupOrderShareUrl(joinCode: string) {
  const appUrl = getEnvVars().APP_URL as string;

  return new URL(`/g/${joinCode}`, appUrl).toString();
}

function stripShareUrlProtocol(url: string) {
  const { host, pathname, search } = new URL(url);

  return `${host}${pathname}${search}`;
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  closeA11yLabel: {
    defaultMessage: 'Close',
    description: 'Group Order Share | Modal | Close button a11y label',
  },
  copyLinkSuccess: {
    defaultMessage: 'Group order link copied',
    description: 'Group Order Share | Modal | Copy link success banner',
  },
  shareSubject: {
    defaultMessage: 'Join my sweetgreen group order',
    description: 'Group Order Share | Modal | Share email subject',
  },
  shareMessage: {
    defaultMessage: 'Join my sweetgreen group order: {share_url}',
    description: 'Group Order Share | Modal | Share message body',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  closeButton: {
    alignSelf: 'flex-end',
  },
  contentContainer: {
    gap: theme.spacing['8'],
    paddingTop: theme.spacing['6'],
    paddingBottom: theme.spacing['8'],
  },
  contentContainerSM: {
    flex: 1,
    paddingHorizontal: theme.spacing['8'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type GroupOrderShareScreenParams = {
  joinCode?: string;
};
